import React from 'react';
import type { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { ModalHeader, useModal } from '@atlaskit/modal-dialog';
import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';

import { ManualTriggerIcon } from '@confluence/icons/entry-points/ManualTriggerIcon';

import { useAutomationMenu } from '../common/ConfluenceManualRulesContainer';

type AutomationModalHeaderProps = {
	contentTitle: string;
	modalTitle: string;
};

export const i18n = defineMessages({
	modalHeaderIconLabel: {
		id: 'automation-menu.modal.header.icon.label',
		defaultMessage: 'Automation modal header icon',
		description: 'A label for the icon in the header of the automation modal.',
	},
	modalHeaderTitle: {
		id: 'automation-menu.modal.header.title',
		defaultMessage: 'Automate',
		description: 'The title for the automation modal',
	},
	modalHeaderLearnMore: {
		id: 'automation-menu.modal.header.learn-more',
		defaultMessage: 'Learn more about manual triggers',
		description:
			'The text for a link that navigates to documentation about automation manual triggers.',
	},
	modalHeaderContextHint: {
		id: 'automation-menu.modal.header.context-hint',
		defaultMessage: 'Apply an automation to <b>{contentTitle}</b>.',
		description:
			'An instruction prompting a user to pick an automation to execute. The contentTitle is the name of the content that the automation will run on.',
	},
	modalHeaderContextDescription: {
		id: 'automation-menu.modal.header.description',
		defaultMessage:
			'The available selections are controlled by Confluence and space administrators.',
		description:
			'A description to users indicating that the available automations are determined by administrators of the site or space.',
	},
});

const iconStyle = xcss({
	marginRight: 'space.150',
	backgroundColor: 'color.background.accent.green.subtlest',
	borderRadius: 'border.radius',
});

const contextHintStyle = xcss({
	marginTop: 'space.150',
});

export const AutomationModalHeader: FC<AutomationModalHeaderProps> = ({
	contentTitle,
	modalTitle,
}) => {
	const { formatMessage } = useIntl();

	const { initialised, rules } = useAutomationMenu();
	const { titleId } = useModal();

	const showHint = initialised && rules.length > 0;
	const contextHint = formatMessage(i18n.modalHeaderContextHint, {
		contentTitle,
		b: (chunks: React.ReactNode[]) => <Text weight="bold">{chunks}</Text>,
	});

	return (
		<ModalHeader hasCloseButton>
			<Stack>
				<Inline alignBlock="center">
					<Box xcss={iconStyle} paddingInline="space.050" paddingBlock="space.025">
						<ManualTriggerIcon
							size="small"
							label={formatMessage(i18n.modalHeaderIconLabel)}
							primaryColor={token('color.icon.accent.green')}
						/>
					</Box>
					<Heading size="medium" id={titleId}>
						{modalTitle}
					</Heading>
				</Inline>
				{showHint && (
					<Box xcss={contextHintStyle}>
						{contextHint}
						<div>{formatMessage(i18n.modalHeaderContextDescription)}</div>
					</Box>
				)}
			</Stack>
		</ModalHeader>
	);
};
